import { useTranslator } from "@eo-locale/react";

import { useCallback, useEffect, useRef } from "react";

import { useRouter } from "next/router";

import Cookies from "js-cookie";

import {
  HStack,
  Box,
  Text,
  Heading,
  useToast,
  useDisclosure,
  TemplateModal as UnverifiedDisabledModal
} from "@/storybook-components";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

import {
  fetchIdeas,
  fetchIdea,
  fetchComments,
  fetchCategories
} from "@/redux/features/brainstormers/thunk";
import {
  resetIdea,
  resetComments,
  resetIdeaError,
  resetBrainstormersError,
  setIdeaTransitionDrawer
} from "@/redux/features/brainstormers/slice";
import useInfiniteScroll from "@/hooks/useInfiniteScroll";
import type { IContentParams } from "@/types";
import AllAlerts from "../common/all-alerts";
import ListView from "./list-view";

import UnverifiedUserModal from "@/components/unverified-user/modal";
import {
  setUnverifiedModalOpen,
  showUnverifiedDisabledModal,
  setModalStatus
} from "@/redux/features/unverified-modal/slice";
import { setTriggerPoint } from "@/redux/features/unverified-modal/slice";
import { getVotingStatus } from "@/helpers/ideas";

import {
  fetchCurrentSessionInformation,
  saveVisitInfo
} from "@/redux/features/visits-logging/thunk";
import { getToolLocaleKeyFromLayout } from "@/helpers/get-tool-locale-key-from-layout";
import { toSentenceCase } from "@/helpers/to-sentence-case";

const Ideas = ({
  count,
  onOpen,
  manageVotes,
  ideas,
  params,
  pageNumber,
  setPageNumber,
  manageBudgeting
}): JSX.Element => {
  const router = useRouter();
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const modalDisclosure = useDisclosure();
  const UnverifiedDisabledModalDisclosure = useDisclosure();
  const {
    idea,
    ideaError,
    brainstormer,
    ideas: ideasList,
    brainstormersError
  } = useAppSelector((state) => state.brainstormers);

  const {
    layout,
    votingStartAt,
    votingEndAt,
    allowUnverifiedParticipation,
    state: brainStormerState,
    toolConfigurationAttributes
  } = brainstormer;
  const listingHeader =
    toolConfigurationAttributes?.preferences?.listingHeader || "";
  const contributionName =
    toolConfigurationAttributes?.preferences?.contributionName || "";

  const { project } = useAppSelector((state) => state.project);
  const { sessionInfo } = useAppSelector(
    (state) => state.visitsLogging.sessionInfo
  );
  const { triggerPoint } = useAppSelector((state) => state.unverifiedModal);
  const { triggeredSource } = triggerPoint;

  const pushIndividualIdeaVisitLogs = async () => {
    if (!sessionInfo?.sessionId) {
      await dispatch(fetchCurrentSessionInformation());
    }

    await dispatch(
      saveVisitInfo({
        itemType: "idea",
        pageType: "Project",
        itemId: Number(router.query.idea),
        pageId: Number(project?.id),
        visitedUrl: window.location.href,

        userAgent: navigator.userAgent
      })
    );
  };

  useEffect(() => {
    toast.closeAll();
    if (router.query.idea && !ideaError && !idea?.id)
      fetchSelectedIdea(router.query.idea);
    if (idea?.id && !idea?.deleted) {
      onOpen();
      pushIndividualIdeaVisitLogs();
    }
  }, []);

  const { projectId, brainstormerId } = router.query as {
    projectId: string;
    brainstormerId: string;
  };

  useEffect(() => {
    if (projectId && brainstormerId) {
      dispatch(fetchCategories({ projectId, brainstormerId }));
    }
  }, [projectId, brainstormerId, dispatch]);

  const contributionNameLocale =
    contributionName.trim() ||
    translate(`tools.${getToolLocaleKeyFromLayout(layout)}.contributionName`);

  useEffect(() => {
    if ((ideaError || brainstormersError) && !toast.isActive("idea-toast")) {
      if (idea?.deleted) {
        toast({
          status: "error",
          id: "idea-toast",
          description: toSentenceCase(
            translate("tools.deletedContributionError", {
              contributionName: contributionNameLocale
            })
          )
        });

        return;
      }

      if (ideaError === "Not Found") {
        toast({
          status: "error",
          id: "idea-toast",
          description: toSentenceCase(
            translate("tools.notFoundContributionError", {
              contributionName: contributionNameLocale
            })
          )
        });

        return;
      }

      toast({
        status: "error",
        id: "idea-toast",
        title: translate("errors.somethingWentWrong"),
        description: toSentenceCase(
          translate("tools.pleaseCheckInternetConnection", {
            contributionName: contributionNameLocale
          })
        )
      });
    }
    dispatch(resetIdeaError());
    dispatch(resetBrainstormersError());
  }, [ideaError, brainstormersError, idea, toast, translate, dispatch]);

  const fetchSelectedIdea = async (ideaId) => {
    toast.closeAll();
    dispatch(resetIdeaError());
    await dispatch(resetIdea());
    await dispatch(resetComments());

    onOpen();
    await dispatch(setIdeaTransitionDrawer("contribution"));
    const timer = setTimeout(async () => {
      await updateRouter(ideaId);
      await dispatch(
        fetchIdea({
          projectId,
          brainstormerId,
          ideaId
        })
      );

      if (idea?.id) {
        await dispatch(
          fetchComments({
            projectId,
            brainstormerId,
            ideaId
          })
        );
      }
      pushIndividualIdeaVisitLogs();
    }, 600);

    return () => {
      // eslint-disable-next-line no-undef
      clearTimeout(timer);
    };
  };

  const updateRouter = async (ideaId: string) => {
    router.query.idea = ideaId;
    router.replace(router, "", { shallow: true });
  };

  /**
   * Load More Ideas
   */
  const loadMoreParams: IContentParams = {
    projectId,
    brainstormerId,
    page: pageNumber,
    sortOrder: params.sortOrder,
    sortBy: params.sortBy,
    search: params.search,
    categoryId: params.categoryId
  };
  // eslint-disable-next-line no-unused-vars
  const { loading, data, hasMore, contentRemain } = useInfiniteScroll(
    fetchIdeas,
    loadMoreParams,
    ideas || ideasList,
    pageNumber,
    20,
    params,
    count,
    brainstormersError,
    "ideas-search-node"
  );

  // We need to create observerRef for tracking last Item in the list
  const observerRef = useRef<any>();

  // When ever the last Item is observed this function will run
  const lastItemRef = useCallback(
    (node) => {
      if (loading) return;
      // If we are on the last item of the list we need to disconnect and connect back to the first item of next list
      if (observerRef.current) observerRef.current.disconnect();
      // The Intersection Observer API provides a way to asynchronously observe changes in the intersection of a target
      // element with an ancestor element or with a top-level document's viewport
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPageNumber((prevPageNumber) => prevPageNumber + 1);
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [loading, hasMore]
  );

  const { isModalOpen, setUnverifiedDisabledModalOpen } = useAppSelector(
    (state) => state.unverifiedModal
  );

  const isVotingAllowed =
    (votingStartAt &&
      votingEndAt &&
      getVotingStatus(votingStartAt, votingEndAt) === "active") ||
    getVotingStatus(votingStartAt, votingEndAt) === "ended";

  const { canContribute, visibilityMode, state: projectState } = project;

  useEffect(() => {
    isModalOpen ? modalDisclosure.onOpen() : modalDisclosure.onClose();
    setUnverifiedDisabledModalOpen
      ? UnverifiedDisabledModalDisclosure.onOpen()
      : UnverifiedDisabledModalDisclosure.onClose();
  }, [isModalOpen, setUnverifiedDisabledModalOpen]);

  const getRoute = (route: "login" | "register") => {
    const trigger = JSON.parse(Cookies.get("trigger_point"));
    if (trigger?.triggeredSource === "comment") {
      return `/${route}?next=${encodeURIComponent(`/${project.permalink}/brainstormers/${brainstormer.permalink}?idea=${trigger?.id}&comment=true`)}`;
    } else if (trigger?.triggeredSource === "vote") {
      return `/${route}?next=${encodeURIComponent(router.asPath)}`;
    } else {
      return `/${route}?next=${encodeURIComponent(`/${project.permalink}/brainstormers/${brainstormer.permalink}`)}`;
    }
  };

  const onCloseUnverifiedDisabledModal = () => {
    dispatch(
      setTriggerPoint({ ...triggerPoint, isTriggeredPointActive: false })
    );
    dispatch(showUnverifiedDisabledModal(false));
    dispatch(setModalStatus("cancel"));
  };

  return (
    <Box pb={6} position="relative" data-testid="ideas-page">
      {/* Scheduled Voting Alert when ideas has scheduled voting */}
      <AllAlerts />
      <UnverifiedDisabledModal
        data-testid="ideas-unverified-disabled-modal"
        header={
          triggeredSource === "vote"
            ? translate("brainstormers.addYourVote")
            : translate("brainstormers.haveYourSay")
        }
        body={translate("brainstormers.unverifiedDisabledModalBodyText")}
        open={UnverifiedDisabledModalDisclosure.open}
        onClose={onCloseUnverifiedDisabledModal}
        buttonPrimary={translate("login.signIn")}
        buttonSecondary={translate("brainstormers.createAnAccount")}
        onPrimaryClickHandler={() => {
          router.push(getRoute("login"), undefined, { shallow: true });
        }}
        onSecondaryClickHandler={() => {
          router.push(getRoute("register"), undefined, { shallow: true });
        }}
        fullSizedModal={false}
        fullWidthButtons
      />
      <UnverifiedUserModal
        data-testid="ideas-unverified-user-modal"
        modalHeader={
          triggeredSource === "vote"
            ? translate("brainstormers.addYourVote")
            : translate("brainstormers.haveYourSay")
        }
        open={modalDisclosure.open}
        onClose={() => {
          dispatch(setUnverifiedModalOpen(false));
        }}
      />
      {/* Heading */}
      <HStack
        as="section"
        justifyContent="space-between"
        color="gray.900"
        data-testid="ideas-heading"
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Heading
            as="h2"
            fontWeight="bold"
            fontSize="xl"
            data-testid="ideas-listing-header"
          >
            {toSentenceCase(
              listingHeader.trim() ||
                translate(
                  `tools.${getToolLocaleKeyFromLayout(layout)}.listingHeader`
                )
            )}
          </Heading>
          <Text
            fontWeight="semibold"
            fontSize="sm"
            bgColor="white"
            color="gray.800"
            borderRadius={6}
            gap={2}
            px={2}
            data-testid="ideas-count"
          >
            {count}
          </Text>
        </Box>
      </HStack>
      {/* List View of Ideas */}
      <ListView
        ideaList={ideas || ideasList}
        lastItemRef={lastItemRef}
        loading={loading}
        contentToLoad={contentRemain}
        fetchSelectedIdea={fetchSelectedIdea}
        manageVotes={manageVotes}
        manageBudgeting={manageBudgeting}
        allowUnverifiedParticipation={allowUnverifiedParticipation}
        canContribute={canContribute}
        projectVisibility={visibilityMode}
        isVotingAllowed={isVotingAllowed}
        brainStormerState={brainStormerState}
        projectState={projectState}
      />
    </Box>
  );
};

export default Ideas;
